import { useEffect, useRef, useState } from "react";
import { Sparkles, ChevronsLeftRight } from "lucide-react";
import { APP_URL } from "@/lib/constants";

const COATS = [
  { img: "/media/tryon-coat-1.jpg", result: "/media/tryon-result-1.jpg", name: "Кашемировое пальто с мехом лисы" },
  { img: "/media/tryon-coat-2.jpg", result: "/media/tryon-result-2.jpg", name: "Пальто-кимоно из 100% шерсти" },
  { img: "/media/tryon-coat-3.jpg", result: "/media/tryon-result-3.jpg", name: "Пальто с вязаными рукавами" },
];

export const AiTryOnDemo = () => {
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [slider, setSlider] = useState(100);
  const stageRef = useRef(null);
  const wrapRef = useRef(null);
  const genTimer = useRef(null);
  const rafRef = useRef(null);
  const dragging = useRef(false);

  const animateSlider = (from, to) => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const dur = 900;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - t, 3);
      setSlider(from + (to - from) * e);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const select = (i) => {
    if (phase === "generating" && selected === i) return;
    clearTimeout(genTimer.current);
    cancelAnimationFrame(rafRef.current);
    setSelected(i);
    setPhase("generating");
    setSlider(100);
    genTimer.current = setTimeout(() => {
      setPhase("done");
      animateSlider(100, 62);
    }, 2000);
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          select(0);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(genTimer.current);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const posFromEvent = (e) => {
    const rect = stageRef.current.getBoundingClientRect();
    return Math.min(96, Math.max(4, ((e.clientX - rect.left) / rect.width) * 100));
  };

  const onPointerDown = (e) => {
    if (phase !== "done") return;
    dragging.current = true;
    cancelAnimationFrame(rafRef.current);
    stageRef.current.setPointerCapture(e.pointerId);
    setSlider(posFromEvent(e));
  };

  const onPointerMove = (e) => {
    if (dragging.current) setSlider(posFromEvent(e));
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  const coat = selected !== null ? COATS[selected] : null;

  return (
    <div
      ref={wrapRef}
      data-testid="tryon-demo"
      className="rounded-3xl p-5 md:p-6"
      style={{ background: "var(--ink)", border: "1px solid rgba(255,171,0,0.3)", boxShadow: "0 30px 80px rgba(6,4,10,0.25)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 text-white font-semibold text-[15px]" style={{ letterSpacing: "-0.01em" }}>
          <Sparkles size={16} style={{ color: "var(--accent)" }} />
          AI-примерочная
        </span>
        <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--accent)" }}>
          <span className="proof-dot w-[6px] h-[6px] rounded-full" style={{ background: "#3ddc84" }} />
          Демо
        </span>
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40 mb-3">
        1. Выберите изделие
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {COATS.map((c, i) => (
          <button
            key={i}
            data-testid={`tryon-coat-${i}`}
            onClick={() => select(i)}
            className="group relative rounded-xl overflow-hidden aspect-[3/4] transition-transform duration-300 hover:scale-[1.03] active:scale-[0.97]"
            style={{
              border: selected === i ? "2px solid var(--accent)" : "2px solid rgba(255,255,255,0.12)",
              boxShadow: selected === i ? "0 0 20px rgba(255,171,0,0.35)" : "none",
            }}
          >
            <img src={c.img} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
            {selected === i && (
              <span
                className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: "var(--accent)" }}
              >
                <Sparkles size={11} color="#2a1a02" />
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40 mb-3">
        2. Результат за секунды
      </div>
      <div
        ref={stageRef}
        data-testid="tryon-stage"
        className="relative rounded-2xl overflow-hidden aspect-[3/4] select-none"
        style={{ background: "#0d0a12", touchAction: "none", cursor: phase === "done" ? "ew-resize" : "default" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <img
          src="/media/tryon-before.jpg"
          alt="Фото до примерки"
          className="absolute inset-0 w-full h-full object-contain"
          draggable="false"
        />

        {coat && (
          <div
            className="absolute inset-0"
            style={{ clipPath: `inset(0 ${100 - slider}% 0 0)`, opacity: phase === "done" ? 1 : 0, transition: "opacity 0.5s ease" }}
          >
            <img
              src={coat.result}
              alt={coat.name}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ background: "#0d0a12" }}
              draggable="false"
            />
          </div>
        )}

        {phase === "done" && (
          <>
            <div
              className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
              style={{ left: `${slider}%`, background: "var(--accent)", boxShadow: "0 0 12px rgba(255,171,0,0.8)" }}
            />
            <div
              data-testid="tryon-handle"
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center pointer-events-none"
              style={{ left: `${slider}%`, background: "var(--accent)", boxShadow: "0 4px 18px rgba(0,0,0,0.5)" }}
            >
              <ChevronsLeftRight size={18} color="#2a1a02" />
            </div>
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] text-white" style={{ background: "rgba(6,4,10,0.65)", backdropFilter: "blur(4px)" }}>
              После
            </span>
            <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] text-white/70" style={{ background: "rgba(6,4,10,0.65)", backdropFilter: "blur(4px)" }}>
              До
            </span>
          </>
        )}

        {phase === "generating" && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0" style={{ background: "rgba(6,4,10,0.35)" }} />
            <div className="tryon-scanline" />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "rgba(6,4,10,0.85)", border: "1px solid rgba(255,171,0,0.4)" }}>
              <Sparkles size={13} style={{ color: "var(--accent)" }} className="tryon-spark" />
              <span className="text-white text-[12px] font-medium whitespace-nowrap">
                Генерируем ваш образ<span className="tryon-dots" />
              </span>
            </div>
          </div>
        )}
      </div>

      {coat && phase === "done" && (
        <div data-testid="tryon-coat-name" className="mt-4 text-[13px] text-white/70 text-center">
          {coat.name} · <span style={{ color: "var(--accent)" }}>потяните ползунок</span>
        </div>
      )}

      <a
        href={APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="tryon-app-cta"
        className="pw-btn pw-btn-light w-full mt-5 !py-[13px] !text-[14px]"
      >
        <Sparkles size={15} />
        Примерить со своим фото
      </a>
    </div>
  );
};
